import React, { useState } from "react";
import "./DriverLogViewFooter.css";
import { useTranslate, Link, useNotify } from "react-admin";
import { Typography } from "@material-ui/core";
import { ArrayField } from "../../../common/data-grid-fields/ArrayField";
import resourceNames from "../../../../common/data/resourceNames";
import {
  DriverLogDetailedView,
  EventApiFactory,
} from "../../../../common/data/service-proxies";
import { DriverLogField } from "./DriverLogField";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import { ShippingDocsArrayField } from "./ShippingDocsArrayField";
import permissionNames from "../../../../common/auth/access-control/permissionNames";
import { useActionPermissions } from "../../../../common/auth/useActionPermissions";

interface DriverLogFormProps extends React.HTMLAttributes<HTMLDivElement> {
  record: DriverLogDetailedView;
}

export const DriverLogViewFooter = ({
  record: rec,
  ...rest
}: DriverLogFormProps) => {
  const translate = useTranslate();
  const axios = useAxiosInstance();
  const notify = useNotify();
  const [record, setRecord] = useState<DriverLogDetailedView>(rec);
  const permissions = useActionPermissions();
  const hasEditPermission =
    permissions && permissions[permissionNames.hos.logs.update];
  return (
    <div id="driver-log-footer-container">
      <Typography variant="h6">
        {translate("resources.hos-driver-log-view.labels.logForm")}
      </Typography>
      <div className="footer-row">
        <div className="driver-log-footer-info">
          <strong>
            {translate("resources.hos-driver-log-view.labels.driver")}:
          </strong>
          <div>{record.driverFullName}</div>
        </div>
        <div className="driver-log-footer-info">
          <strong>
            {translate("resources.hos-driver-log-view.labels.coDrivers")}:
          </strong>
          {record.coDrivers && (
            <div id="codrivers-list">
              {record.coDrivers.map((coDriver) => (
                <Link
                  key={coDriver.id}
                  to={`/${resourceNames.HOS_DRIVER_LOG_VIEW}/${coDriver.id}/${record.date}`}
                >
                  {coDriver.fullName}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="footer-row">
        <div className="driver-log-footer-info">
          <strong>
            {translate("resources.hos-driver-log-view.labels.vehicle")}:
          </strong>
          {record.vehicles && (
            <ArrayField
              record={record}
              source="vehicles"
              displayMember="name"
              displayedElementsCount={record.vehicles.length}
              emptyArrayDisplay=""
            />
          )}
        </div>
        <div className="driver-log-footer-info">
          <strong>
            {translate("resources.hos-driver-log-view.labels.trailers")}:
          </strong>
          {record.trailers && (
            <ArrayField
              record={record}
              source="trailers"
              displayedElementsCount={record.trailers.length}
              emptyArrayDisplay=""
            />
          )}
        </div>
      </div>
      <div className="footer-row">
        <div className="driver-log-footer-info">
          <strong>
            {translate("resources.hos-driver-log-view.labels.distance")}:
          </strong>
          <div>
            {record.distance} {record.odometerUnits}
          </div>
        </div>
        <div className="driver-log-footer-info">
          <strong>
            {translate("resources.hos-driver-log-view.labels.company")}:
          </strong>
          <div>{record.companyName}</div>
        </div>
      </div>
      <div className="footer-row">
        <div className="driver-log-footer-info">
          <strong>
            {translate("resources.hos-driver-log-view.labels.mainOffice")}:
          </strong>
          <div>{record.companyAddress}</div>
        </div>
        <div className="driver-log-footer-info">
          <strong>
            {translate("resources.hos-driver-log-view.labels.homeTerminal")}:
          </strong>
          <div>{record.companyAddress}</div>
        </div>
      </div>
      <div className="footer-row">
        <DriverLogField
          editable={hasEditPermission}
          insertOnly
          className="driver-log-footer-info"
          label="resources.hos-driver-log-view.labels.shippingDocs"
          render={
            (record.shippingDocs || record.pendingShippingDocs) && (
              <ShippingDocsArrayField record={record} />
            )
          }
          onSave={async (value) => {
            if (!axios) {
              return Promise.reject();
            }

            try {
              const shippingDocs = value.split(",").map((x) => x.trim());
              const response = await EventApiFactory(
                undefined,
                axios.defaults.baseURL,
                axios
              ).apiAppEventShipmentnumberPost({
                shippingDocs,
                driverId: record.driverId!,
                logDate: record.date,
              });
              setRecord({ ...record, pendingShippingDocs: response.data });
              notify(
                "resources.hos-driver-log-view.labels.changeShippingDocsSuccess",
                "success"
              );
            } catch (err) {
              console.error(err);
              notify(
                "resources.hos-driver-log-view.labels.changeShippingDocsFailed",
                "error"
              );
            }
          }}
          value={record.shippingDocs?.reduce(
            (previous, current) =>
              previous ? `${previous}, ${current}` : current,
            ""
          )}
        />
        <DriverLogField
          value={record.notes}
          editable={hasEditPermission}
          className="driver-log-footer-info"
          label="resources.hos-driver-log-view.labels.notes"
          onSave={async (value) => {
            if (!axios) {
              return Promise.reject();
            }

            try {
              const response = await EventApiFactory(
                undefined,
                axios.defaults.baseURL,
                axios
              ).apiAppEventSetdrivingnotePost({
                driverId: record.driverId!,
                logDate: record.date,
                note: value,
              });
              setRecord({ ...record, notes: response.data });
              notify(
                "resources.hos-driver-log-view.labels.setDrivingNoteSuccess",
                "success"
              );
            } catch (err) {
              console.error(err);
              notify(
                "resources.hos-driver-log-view.labels.setDrivingNoteFailed",
                "error"
              );
            }
          }}
        />
      </div>
    </div>
  );
};
